import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["aria-hidden", "aria-label", "index", "onClick"]
const _hoisted_2 = ["aria-label", "href", "title", "onKeydown", "onClick"]
const _hoisted_3 = ["title"]
const _hoisted_4 = {
  key: 1,
  class: "navigation-sidebar-item__submenu"
}
const _hoisted_5 = ["href", "title", "onKeydown", "onClick"]
const _hoisted_6 = ["title"]

import { computed, ComputedRef, onMounted, ref, Ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Drawer as KDrawer, DrawerContent as KDrawerContent } from '@progress/kendo-vue-layout';
import { DrawerProps } from '@progress/kendo-vue-layout';
import { Skeleton } from '@progress/kendo-vue-indicators';
import NavigationSidebarHeader from '@/components/common/navigationSidebar/NavigationSidebarHeader.vue';
import NavigationSidebarFooter from '@/components/common/navigationSidebar/NavigationSidebarFooter.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import { useSpaceStore } from '@/stores/space.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { useAppStore } from '@/stores/app.store';
import useTranslations from '@/composables/useTranslations';
import { CwxBackend } from '@/types/cwxBackend';
import { INavigationSidebarItem } from '@/types/navigationSidebarTypes';
import config from '@/../service/project.config.js';


export default /*@__PURE__*/_defineComponent({
  __name: 'NavigationSidebar',
  props: {
    items: {},
    options: {}
  },
  setup(__props: any) {

const props = __props;

const appStore = useAppStore();
const spaceStore = useSpaceStore();
const navigationStore = useNavigationStore();
const { dir } = storeToRefs(appStore);
const { translate: t } = useTranslations();

const options: Ref<DrawerProps> = ref(props.options);
const isHeaderLoaded = ref(false);

const bottomItemsComputed: ComputedRef<INavigationSidebarItem[]> = computed(() => {
  return props.items.filter((item) => item.accessLevel?.startsWith('Admin'));
});
const drawerItems = computed(() => {
  const realItems = props.items.filter((item) => !item.accessLevel?.startsWith('Admin'));
  if (!isHeaderLoaded.value) {
    const count = realItems.length || 5;
    return Array.from({ length: count }, () => ({ skeleton: true }) as INavigationSidebarItem);
  }
  return realItems;
});

function onHeaderLoaded() {
  isHeaderLoaded.value = true;
}

function itemClasses(item: INavigationSidebarItem) {
  let classes = 'navigation-sidebar-item';
  if (item?.classes) classes += ` ${item.classes}`;
  if (item?.type === CwxBackend.SpaceSidebarType.Separator)
    classes += ' k-drawer-item k-drawer-separator';
  if (item?.subItems && item?.subItems?.length > 0)
    classes += ' navigation-sidebar-item--with-submenu';

  const href = window?.location?.href;
  if (
    item?.isOpen ||
    (navigationStore.$state.sidebarNavigationMenuActiveOverride?.menuItemType &&
      navigationStore.$state.sidebarNavigationMenuActiveOverride?.menuItemType ===
        item?.menuItemType) ||
    (item?.url && href?.includes(item?.url)) ||
    (item?.url && item?.url?.includes('SubmitIdea') && href?.includes('draft'))
  ) {
    classes += ' active';
  }

  return classes;
}

function isHref(item: INavigationSidebarItem) {
  return !!item?.url;
}

function onItemClick(item: INavigationSidebarItem) {
  if (item?.url) {
    window.location.href = config.backend + item?.url;
  } else if (item?.action) {
    item.action();
  }

  if (typeof item.index === 'number') {
    drawerItems.value[item.index].isOpen = !drawerItems.value[item.index].isOpen;
  }
}

function onFooterItemClick(item: INavigationSidebarItem) {
  if (item?.subType === 'toggle') {
    options.value.expanded = !options.value.expanded;
    navigationStore.saveDrawerOptionsToLocalStorage(options.value.expanded === true);
  }
}

function checkStateSidebarExpand() {
  if (props?.items?.length > 0) {
    document.body.classList.toggle('navigation-sidebar-expanded', options.value.expanded);
  }
}

function getActiveIcon(item: INavigationSidebarItem) {
  return item?.props?.iconActive ?? `${item?.props?.icon}-active`;
}

watch(options.value, () => {
  checkStateSidebarExpand();
});

watch(
  () => [spaceStore.isSpaceDetailsLoaded, spaceStore.spaceDetails?.id] as const,
  ([loaded, id]) => {
    if (!loaded) {
      isHeaderLoaded.value = false;
    } else if (!id || id <= 0) {
      isHeaderLoaded.value = true;
    } else {
      isHeaderLoaded.value = false;
    }
  },
  { immediate: true },
);

onMounted(() => {
  checkStateSidebarExpand();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(KDrawer), {
    class: _normalizeClass(["navigation-sidebar", {
      'navigation-sidebar--hidden': __props.items?.length === 0,
      'navigation-sidebar--mini': bottomItemsComputed.value.length > 1,
    }]),
    "data-testid": "space-sidebar",
    expanded: options.value.expanded,
    position: options.value.position,
    mode: options.value.mode,
    mini: options.value.mini,
    items: drawerItems.value,
    item: 'CustomItem',
    "mini-width": options.value.miniWidth,
    width: options.value.width,
    dir: _unref(dir),
    "navigation-header": 'headerTemplate',
    "navigation-footer": 'footerTemplate'
  }, {
    headerTemplate: _withCtx(() => [
      _createVNode(NavigationSidebarHeader, {
        expanded: !!options.value.expanded,
        onSpaceImageLoaded: onHeaderLoaded
      }, null, 8, ["expanded"])
    ]),
    CustomItem: _withCtx((customItemContext) => [
      (_unref(spaceStore)?.spaceDetails?.id == null || _unref(spaceStore)?.spaceDetails?.id > 0)
        ? (_openBlock(), _createElementBlock("li", {
            key: 0,
            role: "menuitem",
            "aria-current": "false",
            "aria-disabled": "false",
            "aria-hidden": 
          customItemContext?.props?.type === _unref(CwxBackend).SpaceSidebarType.Separator
            ? 'true'
            : 'false'
        ,
            "aria-label": `${_unref(t)('Common.Link')} ${customItemContext?.props?.text}`,
            class: _normalizeClass(itemClasses(customItemContext?.props)),
            tabindex: '-1',
            index: customItemContext?.props?.id,
            onClick: customItemContext?.props?.onClick
          }, [
            (customItemContext?.props?.skeleton)
              ? (_openBlock(), _createBlock(_unref(Skeleton), {
                  key: 0,
                  shape: 'rectangle',
                  animation: { type: 'pulse' },
                  tabindex: "-1"
                }))
              : (_openBlock(), _createBlock(Tooltip, {
                  key: 1,
                  filter: () => !options.value.expanded,
                  "class-name": 'navigation-sidebar-tooltip',
                  "open-delay": 0,
                  position: 'right',
                  "anchor-element": 'target',
                  "parent-title": true
                }, {
                  default: _withCtx(() => [
                    (customItemContext?.props?.type !== _unref(CwxBackend).SpaceSidebarType.Separator)
                      ? (_openBlock(), _createElementBlock("a", {
                          key: 0,
                          class: "navigation-sidebar-item__top-menu",
                          "aria-label": `${_unref(t)('Common.Link')} ${customItemContext?.props?.text}`,
                          href: 
              isHref(customItemContext?.props)
                ? _unref(config).backend + `${customItemContext?.props.url}`
                : '#'
            ,
                          title: customItemContext?.props?.text,
                          onKeydown: _withKeys(_withModifiers(($event: any) => (onItemClick(customItemContext?.props)), ["stop","prevent"]), ["enter"]),
                          onClick: _withModifiers(($event: any) => (onItemClick(customItemContext?.props)), ["stop","prevent"])
                        }, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["navigation-sidebar-item--flex-row", {
                expanded:
                  customItemContext?.props?.isOpen &&
                  customItemContext?.props?.subItems?.length > 0,
              }])
                          }, [
                            (customItemContext?.props?.icon)
                              ? (_openBlock(), _createElementBlock("i", {
                                  key: 0,
                                  class: _normalizeClass([
                  customItemContext?.props?.selected
                    ? `cursor-pointer navigation-sidebar-item__menu--icon cwx-old-icon ${customItemContext?.props?.icon}  cwx-icon-before_${getActiveIcon(
                        customItemContext,
                      )}`
                    : `cursor-pointer navigation-sidebar-item__menu--icon cwx-old-icon ${customItemContext?.props?.icon}  cwx-icon cwx-icon-before_${customItemContext?.props?.icon}`,
                ])
                                }, null, 2))
                              : _createCommentVNode("", true),
                            (customItemContext?.props?.text && options.value.expanded)
                              ? (_openBlock(), _createElementBlock("div", {
                                  key: 1,
                                  class: "navigation-sidebar-item__menu--label",
                                  title: customItemContext?.props?.text
                                }, _toDisplayString(customItemContext?.props?.text), 9, _hoisted_3))
                              : _createCommentVNode("", true)
                          ], 2)
                        ], 40, _hoisted_2))
                      : _createCommentVNode("", true),
                    (
              customItemContext?.props?.subItems?.length > 0 && customItemContext?.props?.isOpen
            )
                      ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customItemContext?.props?.subItems, (subItem, indexSubItem) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: indexSubItem,
                              class: "k-drawer-item"
                            }, [
                              _createElementVNode("a", {
                                class: "navigation-sidebar-item--flex-row",
                                href: subItem?.url ? _unref(config).backend + subItem?.url : '#',
                                title: subItem?.text,
                                onKeydown: _withKeys(_withModifiers(($event: any) => (onItemClick(subItem)), ["stop","prevent"]), ["enter"]),
                                onClick: _withModifiers(($event: any) => (onItemClick(subItem)), ["stop","prevent"])
                              }, [
                                _createElementVNode("i", {
                                  class: _normalizeClass(["icon-large icon-circle", `submenu-color-${indexSubItem}`])
                                }, null, 2),
                                (
                    subItem?.text &&
                    options.value.expanded &&
                    subItem?.type !== _unref(CwxBackend).SpaceSidebarType.Separator
                  )
                                  ? (_openBlock(), _createElementBlock("div", {
                                      key: 0,
                                      class: "navigation-sidebar-item__submenu--label",
                                      title: subItem?.text
                                    }, _toDisplayString(subItem?.text), 9, _hoisted_6))
                                  : _createCommentVNode("", true)
                              ], 40, _hoisted_5)
                            ]))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["filter"]))
          ], 10, _hoisted_1))
        : _createCommentVNode("", true)
    ]),
    footerTemplate: _withCtx(() => [
      _createVNode(NavigationSidebarFooter, {
        "sub-items": bottomItemsComputed.value,
        expanded: !!options.value.expanded,
        onItemClick: onFooterItemClick
      }, null, 8, ["sub-items", "expanded"])
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(KDrawerContent), null, {
        default: _withCtx(() => [
          _renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      })
    ]),
    _: 3
  }, 8, ["expanded", "position", "mode", "mini", "items", "mini-width", "width", "class", "dir"]))
}
}

})